import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import invariant from 'tiny-invariant';
import { graphql } from '~/apollo/generated/v4';
import type { PaleoMapPartsFragment } from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { PaleoMapFormFields } from '~/components/upload/paleomap/paleomap-form-fields';
import { useZodForm } from '~/utils/forms';
import { initialPaleoMap, paleoMapSchema } from '~/utils/modules/paleomap-v4';

const UPDATE_PALEO_MAP = graphql(`
  mutation UpdatePaleoMap($id: ID!, $input: PaleoMapUpdateInput!) {
    paleoMapUpdate(id: $id, input: $input) {
      result {
        id
      }
    }
  }
`);

export function UpdatePaleoMapForm({
  paleoMap,
}: {
  paleoMap: PaleoMapPartsFragment;
}) {
  const [updatePaleoMap, { loading, error }] = useMutation(UPDATE_PALEO_MAP);

  const form = useZodForm({
    schema: paleoMapSchema,
    values: initialPaleoMap(paleoMap),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      const res = await updatePaleoMap({
        variables: {
          id: paleoMap.id,
          input: values,
        },
      });
      const updatedId = res.data?.paleoMapUpdate?.result?.id;
      invariant(updatedId, 'Error parsing update response');
      toast.success('Palaeomap updated successfully.');
    } catch (err) {
      console.log('Error updating paleomap', err);
      toast.error('There was a problem updating the palaeomap');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <PaleoMapFormFields />
        <HookFormErrors graphQLError={error} />

        <div className="text-center">
          <Button type="submit" color="primary" loading={loading}>
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
